import {useRef, useState} from 'react';
import {UploadSimple} from '@phosphor-icons/react';
import {VStack} from '@astryxdesign/core/VStack';
import {HStack} from '@astryxdesign/core/HStack';
import {Text} from '@astryxdesign/core/Text';
import {Button} from '@astryxdesign/core/Button';
import {supabase} from '@/lib/supabase/client';
import {isPlaceholderImage} from '@/components/layout/TenantLogo';

/** Поле «фото»: показывает текущую картинку и загружает новую файлом в хранилище студии. */
export function ImageUploadField({
  label,
  value,
  folder,
  tenantId,
  onChange,
  wide = false,
}: {
  label: string;
  value: string;
  folder: 'hero' | 'logo';
  tenantId: string | undefined;
  onChange: (url: string) => void;
  wide?: boolean;
}) {
  const input = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    if (!supabase || !tenantId) {
      setError('Хранилище недоступно. Попробуйте позже.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Выберите картинку: JPG, PNG или WebP.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Файл больше 8 МБ. Сожмите фото и повторите.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const ext = (file.name.split('.').pop() ?? 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
      const path = `${tenantId}/${folder}/${crypto.randomUUID()}.${ext}`;
      const {error: uploadError} = await supabase.storage.from('tenant-media').upload(path, file, {upsert: false});
      if (uploadError) throw uploadError;
      onChange(supabase.storage.from('tenant-media').getPublicUrl(path).data.publicUrl);
    } catch (cause) {
      setError(cause instanceof Error ? `Не удалось загрузить: ${cause.message}` : 'Не удалось загрузить фото.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <VStack gap={2}>
      <Text type="supporting">{label}</Text>
      <HStack gap={3} vAlign="center">
        {isPlaceholderImage(value) ? (
          <span className={`flex items-center justify-center rounded-xl border border-dashed border-border text-muted-foreground ${wide ? 'h-20 w-32' : 'h-16 w-16'}`}>
            <UploadSimple size={20} aria-hidden />
          </span>
        ) : (
          <img src={value} alt="" className={`rounded-xl border border-border object-cover ${wide ? 'h-20 w-32' : 'h-16 w-16'}`} />
        )}
        <Button
          label={value && !isPlaceholderImage(value) ? 'Заменить' : 'Загрузить'}
          variant="secondary"
          size="sm"
          icon={<UploadSimple size={16} />}
          isLoading={busy}
          onClick={() => input.current?.click()}
        />
        <input
          ref={input}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void upload(file);
          }}
        />
      </HStack>
      {error ? (
        <Text type="supporting" role="alert">
          {error}
        </Text>
      ) : null}
    </VStack>
  );
}
